import {
  Box, Container, Heading, Button, Table, Thead, Tbody, Tr, Th, Td,
  Badge, Spinner, Alert, AlertIcon, HStack, VStack, Text, Input,
  Select, Card, CardBody, IconButton, useToast, Flex, Tooltip
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getListasCompras } from "../services/api";
import { FiEye, FiChevronLeft, FiChevronRight, FiArrowLeft } from "react-icons/fi";

// Badges de status de resposta
const STATUS_RESPOSTA_COLORS = {
  pendente: "gray",
  em_andamento: "blue",
  respondido: "yellow",
  aprovado: "green",
  finalizado: "purple",
  retirado: "teal",
  cancelado: "red"
};

const STATUS_RESPOSTA_LABELS = {
  pendente: "Pendente",
  em_andamento: "Em Andamento",
  respondido: "Respondido",
  aprovado: "Aprovado",
  finalizado: "Finalizado",
  retirado: "Retirado",
  cancelado: "Cancelado"
};

// Badges de status de envio
const STATUS_ENVIO_COLORS = {
  pendente: "gray",
  enviando: "blue",
  enviado: "green",
  erro: "red",
  reenviado: "orange"
};

const STATUS_ENVIO_LABELS = {
  pendente: "Pendente",
  enviando: "Enviando",
  enviado: "Enviado",
  erro: "Erro",
  reenviado: "Reenviado"
};

export default function ListasCompras() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [listas, setListas] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  });

  // Filtros
  const [filtros, setFiltros] = useState({
    fornecedor_id: searchParams.get("fornecedor_id") || "",
    status_resposta: searchParams.get("status_resposta") || "",
    status_envio: searchParams.get("status_envio") || "",
    data_inicio: searchParams.get("data_inicio") || "",
    data_fim: searchParams.get("data_fim") || "",
    page: parseInt(searchParams.get("page")) || 1
  });

  useEffect(() => {
    fetchListas();
  }, [filtros.page]);

  const fetchListas = async () => {
    setLoading(true);
    const params = {};

    Object.keys(filtros).forEach(key => {
      if (filtros[key]) params[key] = filtros[key];
    });

    const data = await getListasCompras(params);
    if (data) {
      setListas(data.data || []);
      setPagination({
        current_page: data.current_page,
        last_page: data.last_page,
        per_page: data.per_page,
        total: data.total
      });
    } else {
      toast({
        title: "Erro ao carregar listas",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
    setLoading(false);
  };

  const handleFiltroChange = (field, value) => {
    setFiltros(prev => ({ ...prev, [field]: value, page: 1 }));
  };

  const handleAplicarFiltros = () => {
    // Atualiza a URL com os filtros
    const params = {};
    Object.keys(filtros).forEach(key => {
      if (filtros[key]) params[key] = filtros[key];
    });
    setSearchParams(params);
    fetchListas();
  };

  const handleLimparFiltros = () => {
    setFiltros({
      fornecedor_id: "",
      status_resposta: "",
      status_envio: "",
      data_inicio: "",
      data_fim: "",
      page: 1
    });
    setSearchParams({});
    setTimeout(() => fetchListas(), 100);
  };

  const handlePageChange = (newPage) => {
    setFiltros(prev => ({ ...prev, page: newPage }));
  };

  const formatarData = (dataString) => {
    if (!dataString) return "-";
    return new Date(dataString).toLocaleDateString('pt-BR');
  };

  const formatarDataHora = (dataString) => {
    if (!dataString) return "-";
    return new Date(dataString).toLocaleString('pt-BR');
  };

  return (
    <Box minH="100vh" bg="#f0f0f0" ml={{ base: 0, md: "64px" }} pt={{ base: "60px", md: 6 }} pb={6} px={4}>
      <Container maxW="container.xl">
        {/* Header */}
        <HStack justifyContent="space-between" mb={6} flexWrap="wrap">
          <HStack spacing={2}>
            <IconButton
              icon={<FiArrowLeft />}
              onClick={() => navigate('/listas-compras/dashboard')}
              variant="ghost"
              aria-label="Voltar"
              size="md"
            />
            <Heading size="lg" color="#01695E">Listas de Compras</Heading>
          </HStack>
          <HStack spacing={3}>
            <Button
              colorScheme="purple"
              variant="outline"
              onClick={() => navigate('/listas-compras/kanban')}
            >
              Kanban
            </Button>
            <Button
              colorScheme="blue"
              variant="outline"
              onClick={() => navigate('/listas-compras/dashboard')}
            >
              Dashboard
            </Button>
            <Button
              colorScheme="green"
              bg="#01695E"
              onClick={() => navigate('/listas-compras/nova')}
            >
              + Nova Lista
            </Button>
          </HStack>
        </HStack>

        {/* Filtros */}
        <Card mb={6} boxShadow="lg">
          <CardBody>
            <Heading size="sm" mb={4} color="#01695E">Filtros</Heading>
            <VStack spacing={4} align="stretch">
              <Flex gap={4} flexWrap="wrap">
                <Box flex="1" minW="200px">
                  <Text fontSize="sm" mb={1} fontWeight="bold">Status Resposta</Text>
                  <Select
                    value={filtros.status_resposta}
                    onChange={(e) => handleFiltroChange("status_resposta", e.target.value)}
                    placeholder="Todos"
                    size="sm"
                  >
                    <option value="pendente">Pendente</option>
                    <option value="em_andamento">Em Andamento</option>
                    <option value="respondido">Respondido</option>
                    <option value="aprovado">Aprovado</option>
                    <option value="finalizado">Finalizado</option>
                    <option value="retirado">Retirado</option>
                    <option value="cancelado">Cancelado</option>
                  </Select>
                </Box>

                <Box flex="1" minW="200px">
                  <Text fontSize="sm" mb={1} fontWeight="bold">Status Envio</Text>
                  <Select
                    value={filtros.status_envio}
                    onChange={(e) => handleFiltroChange("status_envio", e.target.value)}
                    placeholder="Todos"
                    size="sm"
                  >
                    <option value="pendente">Pendente</option>
                    <option value="enviando">Enviando</option>
                    <option value="enviado">Enviado</option>
                    <option value="erro">Erro</option>
                    <option value="reenviado">Reenviado</option>
                  </Select>
                </Box>

                <Box flex="1" minW="200px">
                  <Text fontSize="sm" mb={1} fontWeight="bold">Data Início</Text>
                  <Input
                    type="date"
                    value={filtros.data_inicio}
                    onChange={(e) => handleFiltroChange("data_inicio", e.target.value)}
                    size="sm"
                  />
                </Box>

                <Box flex="1" minW="200px">
                  <Text fontSize="sm" mb={1} fontWeight="bold">Data Fim</Text>
                  <Input
                    type="date"
                    value={filtros.data_fim}
                    onChange={(e) => handleFiltroChange("data_fim", e.target.value)}
                    size="sm"
                  />
                </Box>
              </Flex>

              <HStack spacing={3}>
                <Button
                  colorScheme="orange"
                  bg="#F68633"
                  onClick={handleAplicarFiltros}
                  size="sm"
                >
                  Aplicar Filtros
                </Button>
                <Button
                  variant="outline"
                  onClick={handleLimparFiltros}
                  size="sm"
                >
                  Limpar
                </Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Tabela */}
        <Card boxShadow="lg">
          <CardBody>
            {loading ? (
              <VStack py={8}>
                <Spinner size="xl" color="#F68633" thickness="4px" />
                <Text color="#01695E">Carregando listas...</Text>
              </VStack>
            ) : listas.length === 0 ? (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                Nenhuma lista encontrada com os filtros aplicados
              </Alert>
            ) : (
              <>
                <Box overflowX="auto">
                  <Table variant="simple" size="sm">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th>Data</Th>
                        <Th>Fornecedor</Th>
                        <Th>Itens</Th>
                        <Th>Respondidos</Th>
                        <Th>Status Resposta</Th>
                        <Th>Status Envio</Th>
                        <Th>Enviado Em</Th>
                        <Th>Ação</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {listas.map((lista) => (
                        <Tr key={lista.id} _hover={{ bg: "gray.50" }}>
                          <Td fontWeight="bold">{formatarData(lista.data_lista)}</Td>
                          <Td>
                            <Text fontWeight="bold" color="#01695E">
                              {lista.fornecedor?.pessoa?.nome || `Fornecedor #${lista.fornecedor_id}`}
                            </Text>
                          </Td>
                          <Td>
                            <Badge colorScheme="blue">{lista.quantidade_itens}</Badge>
                          </Td>
                          <Td>
                            <Badge colorScheme={lista.quantidade_itens_respondidos === lista.quantidade_itens ? "green" : "orange"}>
                              {lista.quantidade_itens_respondidos || 0}
                            </Badge>
                          </Td>
                          <Td>
                            <HStack spacing={1}>
                              <Badge colorScheme={STATUS_RESPOSTA_COLORS[lista.status_resposta]}>
                                {STATUS_RESPOSTA_LABELS[lista.status_resposta] || lista.status_resposta}
                              </Badge>
                              {!!lista.finalizada_automaticamente && (
                                <Tooltip
                                  label={`Aprovada e finalizada automaticamente pelo sistema${lista.aprovado_em ? ` em ${formatarDataHora(lista.aprovado_em)}` : ''} — nenhuma ação necessária`}
                                  placement="top" hasArrow>
                                  <Badge colorScheme="cyan" variant="solid">🤖 Auto</Badge>
                                </Tooltip>
                              )}
                            </HStack>
                          </Td>
                          <Td>
                            <Badge colorScheme={STATUS_ENVIO_COLORS[lista.status_envio]}>
                              {STATUS_ENVIO_LABELS[lista.status_envio] || lista.status_envio}
                            </Badge>
                          </Td>
                          <Td fontSize="xs">{formatarDataHora(lista.enviado_em)}</Td>
                          <Td>
                            <IconButton
                              icon={<FiEye />}
                              colorScheme="orange"
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/listas-compras/${lista.id}`)}
                              aria-label="Ver detalhes"
                            />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>

                {/* Paginação */}
                <HStack justifyContent="space-between" mt={4} flexWrap="wrap">
                  <Text fontSize="sm" color="gray.600">
                    Mostrando {listas.length} de {pagination.total} listas
                  </Text>
                  <HStack>
                    <IconButton
                      icon={<FiChevronLeft />}
                      onClick={() => handlePageChange(pagination.current_page - 1)}
                      isDisabled={pagination.current_page === 1}
                      size="sm"
                      aria-label="Página anterior"
                    />
                    <Text fontSize="sm">
                      Página {pagination.current_page} de {pagination.last_page}
                    </Text>
                    <IconButton
                      icon={<FiChevronRight />}
                      onClick={() => handlePageChange(pagination.current_page + 1)}
                      isDisabled={pagination.current_page === pagination.last_page}
                      size="sm"
                      aria-label="Próxima página"
                    />
                  </HStack>
                </HStack>
              </>
            )}
          </CardBody>
        </Card>
      </Container>
    </Box>
  );
}
